import { useCallback } from "react";
import * as XLSX from "xlsx";
import {
  BiddingItemStatusConst,
  ListingCategoryConst,
  ProductionHistoryConst,
} from "../constants";
import type { ExcelInfoItem } from "../types";
import { ExcelItemsConst } from "../constants/ExcelItemsConst";
import { useApiClient } from "./useApiClient";
import { TEA_CATEGORY } from "../constants/TeaCategoryConst";

// Excelデータの1行分の型（文字列の配列）
type ExcelRow = string[];
// Excelデータ全体の型（行の配列）
type ExcelData = ExcelRow[];

// 結果格納
export interface convertExcelResult {
  data: ExcelInfoItem[];
  message: string;
  result: boolean;
}

export const useExcelQuery = () => {
  const api = useApiClient();

  const DownloadExcel = useCallback(
    async (fileName: string = "template.xlsx") => {
      try {
        // ※毎回新しいAPIとして認識させることで、古いExcelを取得しないようにする
        const response = await api.get(`/Excel/download`, {
          responseType: "blob",
          headers: {
            'Cache-Control': 'no-cache',  // 新しいリクエストを強制する（HTTP/1.1用）
            'Pragma': 'no-cache',  // 古いブラウザやプロキシ用（HTTP/1.0用）
            'Expires': '0',  // すぐに有効期限が切れるように設定
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        });

        // ガード処理: 万が一エラーメッセージ(JSON)が返ってきている場合はダウンロードさせない
        if (response.data.type === "application/json") {
          const textData = await response.data.text();
          const errorData = JSON.parse(textData);

          throw new Error(errorData.message || "サーバーエラーが発生しました");
        }

        // Blobの生成（念の為、型指定を明示）
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // ダウンロード実行
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // クリーンアップ
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (ex) {
        console.error("Excelのダウンロードに失敗しました。", ex);
        throw ex;
      }
    },
    [api],
  );

  /**
   * 結果Excelのダウンロード処理
   * @param blob 
   * @param fileName 
   */
  const DownloadResultExcel = (base64: string, fileName: string) => {
    // Base64をデコードしてバイナリ文字列にする
    const bin = atob(base64);
    
    // バイナリ文字列をUint8Array（バイト配列）に変換
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    // Blobオブジェクトを作成 (ExcelのMIMEタイプ指定)
    const blob = new Blob([buffer.buffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });

    // ダウンロードリンクを生成してクリック
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // 後始末
  };

  /**
   * Excelファイルデータ変換
   * @param file
   * @returns
   */
  const ConvertExcel = async (file: File, biddingDate: number | undefined): Promise<convertExcelResult> =>{
    let result: convertExcelResult = {
      data: [],
      message: "",
      result: false,
    };

    let excelData: ExcelData | null = [];

    //#region Excelファイルデータ抽出
    if (!file) {
      result.data = [];
      result.message = "ファイルデータが存在していません。";
      result.result = false;
      return result;
    }

    // MIMEタイプまたは拡張子の簡易チェック
    const validExtensions = [".xlsx", ".xls"];
    const isExcel = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );

    if (!isExcel) {
      // setError('サポートされていないファイル形式です。.xlsx, .xlsのみ対応しています。');
      result.data = [];
      result.message = "サポートされていないファイル形式です。.xlsx, .xlsのみ対応しています。";
      result.result = false;
      return result;
    }

    try {
      // ファイルの読み込み処理
      const data = await file.arrayBuffer();

      // Workbookの読み込み
      const workbook = XLSX.read(data, { type: "array" });

      // 最初のシート名を取得
      const sheetName = workbook.SheetNames[0];

      // シートが存在しない場合のエラーハンドリング
      if (!sheetName) {
        // throw new Error("Excelファイル内にシートが見つかりません。");
        result.data = [];
        result.message = "Excelファイル内にシートが見つかりません。";
        result.result = false;
        return result;
      }

      const worksheet = workbook.Sheets[sheetName];

      // シートデータを2次元配列（string[][]）に変換
      // header: 1 を指定することで、キーバリュー形式ではなく配列の配列として取得
      // defval: "" を指定することで、空セルを空文字として扱う
      const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false, // すべての値を文字列としてフォーマットして取得
      }) as string[][];

      // 1行目はヘッダー、2-3行目はスキップ、4行目以降をデータとして結合
      if (jsonData.length > 0) {
        const headerRow = jsonData[0]; // 1行目 (Index 0)
        // 2行目(Index 1)、3行目(Index 2) はスキップ
        // 4行目 (Index 3) 以降
        const dataRows = jsonData.slice(3).filter((row) => {
          // 中身が全てnullもしくは空の場合は除外
          const hasValue = (row as string[]).some((cell) => {
            return cell !== null && cell !== undefined && String(cell).trim() !== '';
          });
          return hasValue;
        }); 
        
        excelData = [headerRow, ...dataRows];
      } else {
        excelData = [];
      }
    } catch (err) {
      console.error(err);
      // setError('ファイルの解析中にエラーが発生しました。');
      result.data = [];
      result.message = "ファイルの解析中にエラーが発生しました。";
      result.result = false;
      return result;
    }
    //#endregion

    //#region ExcelデータをBiddingInfoItemに整形
    let today = new Date().toLocaleDateString("sv-SE").replace(/-/g, ""); // 本日日付

    // データがヘッダー行しかない、または空の場合は空配列を返す
    if (!excelData || excelData.length < 2) {
      result.data = [];
      result.message = "データが入力されていません。";
      result.result = false;
      return result;
    }

    result.data = excelData.slice(1)
                    .map((row) => {

                      return {
                        itemId: "",
                        receptionNumber: "",
                        receptionDate: today || "", //受付日(当日)
                        biddingDate: String(biddingDate) || "", //入札日(直近の開催日)
                        subItems: [], 
                        
                        //Excel側の並び順
                        sellerCode: row[ExcelItemsConst.SellerCode] || "", sellerName: "", 
                        farmerCode: row[ExcelItemsConst.FarmerCode] || "", farmerName: row[ExcelItemsConst.FarmerName], 
                        isFarmerHidden: row[ExcelItemsConst.IsFarmerHidden] || "",
                        teaSeasonCode: row[ExcelItemsConst.TeaSeasonCode] || "",  //茶期
                        teaTypeCode: row[ExcelItemsConst.TeaTypeCode] || "",  //茶種
                        brand: row[ExcelItemsConst.Brand] || "",  //荷印
                        varietyCode: row[ExcelItemsConst.VarietyCode] || "", //品種コード)
                        varietyName: row[ExcelItemsConst.VarieryNameFree] || "", //品種名
                        gapCode: row[ExcelItemsConst.GapCode] || "",
                        caltivationCategory:  row[ExcelItemsConst.CaltivationCategoryCode] || "", //栽培区分
                        capacity1: row[ExcelItemsConst.Capacity1] || "",  //容量①
                        quantity1: row[ExcelItemsConst.Quantity1] || "",  //本数①
                        capacity2: row[ExcelItemsConst.Capacity2] || "",  //容量②
                        quantity2: row[ExcelItemsConst.Quantity2] || "",  //本数②
                        bidMinPrice: row[ExcelItemsConst.BidMinPrice] || "", //最低落札値
                        biko: row[ExcelItemsConst.Biko] || "", //備考

                        //固定値
                        productionHistory: String(ProductionHistoryConst.YES), //生産履歴「有」固定
                        listingCategory: String(ListingCategoryConst.BIDDING), //出品区分「通常入札」固定
                        status: String(BiddingItemStatusConst.INPUT), //ステータス「入力中」固定
                        teaCategory: String(TEA_CATEGORY.ARACHA), //茶区分

                        //Excel上にはあるが、Codeプロパティ側でそのまま渡すため空
                        teaSeasonName: "",
                        teaTypeName: "",
                        gapName: "",
                      };
      });

    result.message = "処理が完了しました。";
    result.result = true;

    return result;
  };

  return {
    DownloadExcel,
    DownloadResultExcel,
    ConvertExcel
  };
};
